/**
 * React Query hooks for Meta connection management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getMetaConnection,
  getMetaAuthorizeUrl,
  disconnectMeta,
  getMetaAccounts,
  finalizeMetaConnection,
  cancelPendingSession,
} from '@/services/meta';

/**
 * Query key factory for Meta connection
 */
export const metaConnectionKeys = {
  all: ['meta-connection'] as const,
  connection: (teamId: string) => [...metaConnectionKeys.all, teamId] as const,
  pendingAccounts: (sessionId: string) => [...metaConnectionKeys.all, 'pending', sessionId] as const,
};

/**
 * Hook to fetch Meta connection for current team
 */
export function useMetaConnection(teamId: string) {
  return useQuery({
    queryKey: metaConnectionKeys.connection(teamId),
    queryFn: async () => {
      const response = await getMetaConnection(teamId);
      return response.connection;
    },
    enabled: !!teamId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to initiate Meta OAuth flow
 */
export function useConnectMeta(teamId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await getMetaAuthorizeUrl(teamId);
      return response;
    },
    onSuccess: (data) => {
      // Redirect to Meta OAuth page
      window.location.href = data.authUrl;
    },
  });
}

/**
 * Hook to disconnect Meta account
 */
export function useDisconnectMeta(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => disconnectMeta(teamId),
    onSuccess: () => {
      // Invalidate connection query to refresh UI
      queryClient.invalidateQueries({ queryKey: metaConnectionKeys.connection(teamId) });
    },
  });
}

// ============================================================================
// ACCOUNT SELECTION HOOKS
// ============================================================================

/**
 * Hook to fetch accounts from pending session
 */
export function useMetaAccounts(sessionId: string | null) {
  return useQuery({
    queryKey: sessionId ? metaConnectionKeys.pendingAccounts(sessionId) : ['disabled'],
    queryFn: async () => {
      if (!sessionId) throw new Error('No session ID');
      const response = await getMetaAccounts(sessionId);
      return response.accounts;
    },
    enabled: !!sessionId,
    staleTime: 4 * 60 * 1000, // 4 minutes (session expires in 5)
    retry: false, // Don't retry - session may have expired
  });
}

/**
 * Hook to finalize Meta connection with selected account
 */
export function useFinalizeMetaConnection(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sessionId, accountId }: { sessionId: string; accountId: string }) => {
      const response = await finalizeMetaConnection(sessionId, accountId);
      return response.connection;
    },
    onSuccess: () => {
      // Invalidate connection query to refresh UI
      queryClient.invalidateQueries({ queryKey: metaConnectionKeys.connection(teamId) });
    },
  });
}

/**
 * Hook to cancel pending session
 */
export function useCancelPendingSession() {
  return useMutation({
    mutationFn: async (sessionId: string) => {
      return cancelPendingSession(sessionId);
    },
  });
}
